import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import type { GlossaryTermCreate, GlossaryTermPatch } from "./glossary-model";

const STALE_MS = 10_000;

const glossaryKey = (ws: string, agentId: string) => ["ws", ws, "agents", agentId, "glossary"] as const;

/** `GET /v1/glossary/terms?agent_id=&q=`: the terms that apply to this agent (the workspace's and its own), 50 a page. */
export function useGlossaryTerms(agentId: string, query: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: [...glossaryKey(ws, agentId), query.trim()],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/glossary/terms", { params: { query: { agent_id: agentId, q: query.trim() || undefined, cursor: pageParam } } })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** Add, change and remove a term; every success refreshes the list. */
export function useGlossaryMutations(agentId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: glossaryKey(ws, agentId) });
  const create = useMutation({
    mutationFn: (body: GlossaryTermCreate) => unwrap(api.POST("/v1/glossary/terms", { body })),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: GlossaryTermPatch }) =>
      unwrap(api.PATCH("/v1/glossary/terms/{term_id}", { params: { path: { term_id: id } }, body })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/glossary/terms/{term_id}", { params: { path: { term_id: id } } })),
    onSuccess: refresh,
  });
  return { create, update, remove };
}
